'use client';

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import importedStyles from '../public/translations/styles.json';

type StyleEntry = Record<string, string>;
type StylesByLanguage = Record<string, Record<string, StyleEntry>>;
export type StyleEditorMode = 'style' | 'text';

// Properties that need a block-ish box to take effect on inline elements
const BOX_KEYS = new Set([
    'width', 'maxWidth', 'minWidth', 'height', 'maxHeight', 'minHeight',
    'paddingTop', 'paddingBottom', 'marginTop', 'marginBottom',
    'borderWidth', 'borderRadius', 'borderColor', 'display',
]);

interface StyleContextType {
    getStyle: (id: string) => React.CSSProperties;
    getRawStyle: (id: string) => StyleEntry;
    updateStyle: (id: string, patch: Record<string, string>) => void;
    resetStyle: (id: string) => void;
    saveStyle: (id: string) => Promise<void>;
    editingStyleId: string | null;
    editorMode: StyleEditorMode;
    anchorEl: HTMLElement | null;
    openStyleEditor: (id: string, anchor?: HTMLElement | null, mode?: StyleEditorMode) => void;
    closeStyleEditor: () => void;
}

const StyleContext = createContext<StyleContextType | undefined>(undefined);

export function StyleProvider({ children }: { children: React.ReactNode }) {
    const { language } = useLanguage();
    const [styles, setStyles] = useState<StylesByLanguage>(importedStyles as StylesByLanguage);
    const [editingStyleId, setEditingStyleId] = useState<string | null>(null);
    const [editorMode, setEditorMode] = useState<StyleEditorMode>('style');
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const stylesRef = useRef(styles);
    const languageRef = useRef(language);

    useEffect(() => {
        stylesRef.current = styles;
    }, [styles]);

    useEffect(() => {
        languageRef.current = language;
    }, [language]);

    // State-only update: live preview without writing the file, so the dev
    // server never hot-reloads mid-editing. Persistence happens in saveStyle.
    const updateStyle = useCallback((id: string, patch: Record<string, string>) => {
        const lang = languageRef.current;
        const prev = stylesRef.current;
        const langMap = { ...(prev[lang] || {}) };
        const merged: StyleEntry = { ...(langMap[id] || {}), ...patch };

        // Drop empty values so cleared controls remove the property
        Object.keys(merged).forEach((key) => {
            if (merged[key] === '' || merged[key] == null) delete merged[key];
        });

        if (Object.keys(merged).length === 0) delete langMap[id];
        else langMap[id] = merged;

        const next = { ...prev, [lang]: langMap };
        stylesRef.current = next;
        setStyles(next);
    }, []);

    // State-only clear; the deletion is committed when Done (saveStyle) is pressed
    const resetStyle = useCallback((id: string) => {
        const lang = languageRef.current;
        const prev = stylesRef.current;
        const langMap = { ...(prev[lang] || {}) };
        delete langMap[id];

        const next = { ...prev, [lang]: langMap };
        stylesRef.current = next;
        setStyles(next);
    }, []);

    // Explicit save (Done button): write the file, then reload so the baked
    // static import and every visitor view pick up the new styles.
    const saveStyle = useCallback(async (id: string) => {
        if (typeof window === 'undefined') return;
        const entry = stylesRef.current[languageRef.current]?.[id] ?? null;
        window.dispatchEvent(new CustomEvent('contentSaving', { detail: { status: 'saving' } }));
        try {
            const response = await fetch('/api/save-style/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ id, language: languageRef.current, styles: entry }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to save');
            }
            window.dispatchEvent(new CustomEvent('contentSaving', { detail: { status: 'saved' } }));
            window.location.reload();
        } catch (error) {
            console.error('Error saving styles:', error);
            window.dispatchEvent(new CustomEvent('contentSaving', { detail: { status: 'error' } }));
        }
    }, []);

    const getRawStyle = useCallback((id: string): StyleEntry => {
        return styles[language]?.[id] || {};
    }, [styles, language]);

    const getStyle = useCallback((id: string): React.CSSProperties => {
        const raw = styles[language]?.[id];
        if (!raw || Object.keys(raw).length === 0) return {};
        const css = { ...raw } as React.CSSProperties;
        // Browsers default border-style to none; a width/color alone would show nothing
        if ((raw.borderWidth || raw.borderColor) && !raw.borderStyle) {
            css.borderStyle = 'solid';
        }
        // Inline elements ignore width/vertical spacing; promote to inline-block
        if (!css.display && Object.keys(raw).some((key) => BOX_KEYS.has(key))) {
            css.display = 'inline-block';
        }
        return css;
    }, [styles, language]);

    const openStyleEditor = useCallback((id: string, anchor?: HTMLElement | null, mode: StyleEditorMode = 'style') => {
        setEditingStyleId(id);
        setAnchorEl(anchor ?? null);
        setEditorMode(mode);
    }, []);

    const closeStyleEditor = useCallback(() => {
        setEditingStyleId(null);
        setAnchorEl(null);
        setEditorMode('style');
    }, []);

    return (
        <StyleContext.Provider value={{ getStyle, getRawStyle, updateStyle, resetStyle, saveStyle, editingStyleId, editorMode, anchorEl, openStyleEditor, closeStyleEditor }}>
            {children}
        </StyleContext.Provider>
    );
}

export function useStyles() {
    const context = useContext(StyleContext);
    if (context === undefined) {
        throw new Error('useStyles must be used within a StyleProvider');
    }
    return context;
}
